import { ROLES } from '@inpart-io/devtools-iap';
import { IS_ROLE, SESSION_MODULE } from '@inpart-io/devtools-vue';
import { dealTypeProperties } from '@inpart-io/padme-iap';
import { type RouteRecordRaw } from 'vue-router';

import { errorTypeCodes } from '@/constants/error-type.constants';
import { ROUTE_DATA } from '@/constants/routes.constants';
import { accountRoute } from '@/pages/account/routes';
import { advancedSearchRoute } from '@/pages/advanced-search/routes';
import { agreementHubBoardRoute, agreementHubMainRoute } from '@/pages/agreement-hub/routes';
import { agreementOverviewRoute } from '@/pages/agreement-overview/routes';
import { allianceHubBoardRoute, allianceHubMainRoute } from '@/pages/alliance-hub/routes';
import { allianceOverviewRoute } from '@/pages/alliance-main-overview/routes';
import { amendmentOverviewRoute } from '@/pages/amendment-overview/routes';
import { briefHubRoute } from '@/pages/brief-hub/routes';
import { briefOverviewRoute } from '@/pages/brief-overview/routes';
import { campaignHubRoute } from '@/pages/campaign-hub/routes';
import { campaignOverviewRoute } from '@/pages/campaign-overview/routes';
import { companyHubBoardRoute, companyHubRoute } from '@/pages/company-hub/routes';
import { companyOverviewRoute } from '@/pages/company-overview/routes';
import { conferenceHubRoute } from '@/pages/conference-hub/routes';
import { conferenceOverviewRoute } from '@/pages/conference-overview/routes';
import { contactHubRoute } from '@/pages/contact-hub/routes';
import { contactOverviewRoute } from '@/pages/contact-overview/routes';
import { dashboardHubBoardRoute, dashboardHubMainRoute } from '@/pages/dashboard-hub/routes';
import { errorPageRoute } from '@/pages/error/routes';
import { evaluationEditionRoute, evaluationReadRoute } from '@/pages/evaluation/routes';
import { exampleHubRoute, exampleOverviewRoute } from '@/pages/example-overview/routes';
import { homeRoute } from '@/pages/home/routes';
import { leadHubBoardRoute, leadHubMainRoute } from '@/pages/lead-hub/routes';
import { leadOverviewRoute, taskOverviewRoute } from '@/pages/lead-overview/routes';
import { meetingHubBoardRoute, meetingHubMainRoute } from '@/pages/meeting-hub/routes';
import { meetingOverviewRoute } from '@/pages/meeting-overview/routes';
import { networkRoute } from '@/pages/network/routes';
import { obligationCreationRoute, obligationEditionRoute } from '@/pages/obligation-creation/routes';
import { obligationHubBoardRoute, obligationHubMainRoute } from '@/pages/obligation-hub/routes';
import { obligationInstanceOverviewRoute } from '@/pages/obligation-instance-overview/routes';
import { platformSettingsRoute } from '@/pages/platform-settings/routes';
import { triggeringEventOverviewRoute } from '@/pages/triggering-event-overview/routes';
import { store } from '@/store';
import { APPLICATION_MODULE } from '@/store/application-module';
import { LOAD_RECENT_LISTS, RETRIEVE_PARTNER_STATUS } from '@/store/application-module/actions';
import { DEAL_TYPE_MODULE } from '@/store/deal-type-module';
import { RETRIEVE_ALL_DEAL_TYPES } from '@/store/deal-type-module/actions';
import { INFO_GROUP_MODULE } from '@/store/infogroup-module';
import { RETRIEVE_INFO_GROUPS } from '@/store/infogroup-module/actions';
import { LEAD_STATUS_CONFIGURATION_MODULE } from '@/store/lead-status-configuration-module';
import { RETRIEVE_ALL_STATUS_CONFIG } from '@/store/lead-status-configuration-module/actions';
import { LIST_BEHAVIOR_CONFIGURATION_MODULE } from '@/store/list-behavior-configuration-module';
import { RETRIEVE_LIST_BEHAVIOR_CONFIGS } from '@/store/list-behavior-configuration-module/actions';
import { PRIVATE_LEAD_BOARD_MODULE } from '@/store/private-lead-board-module';
import { RETRIEVE_DEFAULT_BOARDS } from '@/store/private-lead-board-module/actions';
import { USE_DEAL_WORKFLOW } from '@/store/session-module/getters';
import { TENANT_CONFIGURATION_MODULE } from '@/store/tenant-configuration-module';
import { RETRIEVE_OR_CREATE_TENANT_CONFIG } from '@/store/tenant-configuration-module/actions';
import { USER_PROFILE_MODULE } from '@/store/user-profile-module';
import { RETRIEVE_CURRENT_USER_PROFILE } from '@/store/user-profile-module/actions';

import Main from './main-layout.vue';

const ErrorPage = (): unknown => {
    return import(/* webpackPrefetch: true */ '@/pages/error/error-page.vue');
};

export const mainSubRoutes: RouteRecordRaw[] = [
    accountRoute,
    advancedSearchRoute,
    agreementHubBoardRoute,
    agreementHubMainRoute,
    agreementOverviewRoute,
    allianceHubBoardRoute,
    allianceHubMainRoute,
    allianceOverviewRoute,
    amendmentOverviewRoute,
    briefHubRoute,
    briefOverviewRoute,
    campaignHubRoute,
    campaignOverviewRoute,
    companyHubBoardRoute,
    companyHubRoute,
    companyOverviewRoute,
    contactHubRoute,
    contactOverviewRoute,
    conferenceHubRoute,
    conferenceOverviewRoute,
    dashboardHubBoardRoute,
    dashboardHubMainRoute,
    exampleOverviewRoute,
    exampleHubRoute,
    homeRoute,
    leadHubMainRoute,
    leadOverviewRoute,
    leadHubBoardRoute,
    meetingOverviewRoute,
    meetingHubMainRoute,
    meetingHubBoardRoute,
    networkRoute,
    obligationCreationRoute,
    obligationEditionRoute,
    obligationHubMainRoute,
    obligationHubBoardRoute,
    obligationInstanceOverviewRoute,
    platformSettingsRoute,
    evaluationEditionRoute,
    evaluationReadRoute,
    taskOverviewRoute,
    triggeringEventOverviewRoute,
    // error pages must be at the end
    errorPageRoute,
    {
        name: 'error',
        path: '/:pathMatch(.*)*',
        components: {
            main: ErrorPage
        }
    }
];

export const mainRoute: RouteRecordRaw = {
    ...ROUTE_DATA.MAIN_LAYOUT,
    redirect: {
        name: ROUTE_DATA.HOME_PAGE.name
    },
    component: Main,
    children: mainSubRoutes,
    beforeEnter: async (to, from, next) => {
        try {
            await store.dispatch(`${USER_PROFILE_MODULE}/${RETRIEVE_CURRENT_USER_PROFILE}`);
            if (
                Boolean(store.getters[`${SESSION_MODULE}/${IS_ROLE}`](ROLES.MANAGER)) ||
                Boolean(store.getters[`${SESSION_MODULE}/${IS_ROLE}`](ROLES.CONTRIBUTOR))
            ) {
                const promises = [];
                promises.push(store.dispatch(`${LEAD_STATUS_CONFIGURATION_MODULE}/${RETRIEVE_ALL_STATUS_CONFIG}`));
                promises.push(store.dispatch(`${LIST_BEHAVIOR_CONFIGURATION_MODULE}/${RETRIEVE_LIST_BEHAVIOR_CONFIGS}`));
                promises.push(store.dispatch(`${TENANT_CONFIGURATION_MODULE}/${RETRIEVE_OR_CREATE_TENANT_CONFIG}`));
                promises.push(
                    store.dispatch(`${DEAL_TYPE_MODULE}/${RETRIEVE_ALL_DEAL_TYPES}`, {
                        properties: dealTypeProperties.DEFAULT
                    })
                );
                promises.push(
                    store.dispatch(`${PRIVATE_LEAD_BOARD_MODULE}/${RETRIEVE_DEFAULT_BOARDS}`, {
                        isManager: Boolean(store.getters[`${SESSION_MODULE}/${IS_ROLE}`](ROLES.MANAGER))
                    })
                );
                if (!store.getters[`${SESSION_MODULE}/${USE_DEAL_WORKFLOW}`]) {
                    promises.push(store.dispatch(`${APPLICATION_MODULE}/${RETRIEVE_PARTNER_STATUS}`));
                }
                promises.push(store.dispatch(`${INFO_GROUP_MODULE}/${RETRIEVE_INFO_GROUPS}`));
                promises.push(store.dispatch(`${APPLICATION_MODULE}/${LOAD_RECENT_LISTS}`));
                await Promise.all(promises);
                next();
            } else {
                next({ name: ROUTE_DATA.NO_ACCESS_PAGE.name, params: { id: errorTypeCodes.FORBIDDEN }, replace: true });
            }
        } catch (error) {
            next({ name: ROUTE_DATA.NO_ACCESS_PAGE.name, params: { id: error?.status }, replace: true });
        }
    }
};
